import md5 from "blueimp-md5";
import ExifReader from "exifreader";
import startsWith from "lodash/startsWith";
import BaseUploadProvider from "../../providers/upload/BaseUploadProvider";
import { DispatchCustomizationAction } from "../../states/customization";
import { Size, Unit } from "../../types/customization/box";
import { SelectionPhoto } from "../../types/selection/selection";
import { imageUrlFromSelectionPhoto, THUMBNAIL_FIT_SIZE } from "../../utils/selection/photo";
import { getVariableByEnv } from "../variableByEnv";

export const handleFile = async (
  file: File,
  dispatch: DispatchCustomizationAction,
  uploadProvider: BaseUploadProvider
): Promise<SelectionPhoto | void> => {
  const { type } = file;

  if (!startsWith(type, "image/")) {
    return;
  }

  try {
    // console.time("start without " + file.name);
    // console.time("start with " + file.name);
    const arrayBuffer = await readFileAsArrayBuffer(file);
    const exifOrientation = getExifOrientation(arrayBuffer);

    const dataUrl = await readFileAsDataUrl(file);
    const size = await calcImageSize(dataUrl);
    let thumbnailDataUrl = "data:image/jpeg;base64,";
    let pictureDataUrl = "data:image/jpeg;base64,";
    // console.timeEnd("start without " + file.name);

    if (getVariableByEnv("NODE_ENV") === "development") {
      thumbnailDataUrl = await getResizedDataUrl(dataUrl, exifOrientation, THUMBNAIL_FIT_SIZE);
      pictureDataUrl = await getResizedDataUrl(dataUrl, exifOrientation, 1500);
      // console.timeEnd("start with " + file.name);
    }

    if (exifOrientation && exifOrientation >= 5) {
      const { width: originalWidth, height: originalHeight } = size;
      size.width = originalHeight;
      size.height = originalWidth;
    }

    const localRef = `web:${file.name}:${file.size}o`;
    const selectionPhoto: SelectionPhoto = {
      key: `local-${md5(localRef)}`,
      localRef,
      provider: "upload",
      providerPictureUrl: pictureDataUrl,
      providerRef: localRef,
      providerThumbnailUrl: thumbnailDataUrl,
      status: "uploading",
      ...size,
    };
    if (file.lastModified) {
      selectionPhoto.lastModified = file.lastModified;
    }

    dispatch({
      selectionPhoto,
      type: "addOrUpdateSelectionPhoto",
    });

    return uploadProvider
      .upload(selectionPhoto, file)
      .then((updatedSelectionPhoto) => {
        const successSelectionPhoto: SelectionPhoto = {
          ...updatedSelectionPhoto,
          providerPictureUrl: imageUrlFromSelectionPhoto(updatedSelectionPhoto, "full"),
          providerThumbnailUrl:
            getVariableByEnv("NODE_ENV") !== "development"
              ? imageUrlFromSelectionPhoto(
                  { ...updatedSelectionPhoto, providerThumbnailUrl: undefined },
                  "thumbnail"
                )
              : thumbnailDataUrl, // keep data url for dev
          status: "success",
        };
        dispatch({
          selectionPhoto: successSelectionPhoto,
          type: "addOrUpdateSelectionPhoto",
        });
        uploadProvider.addToFinished(successSelectionPhoto);
        return successSelectionPhoto;
      })
      .catch(() => {
        const errorPhoto: SelectionPhoto = {
          ...selectionPhoto,
          status: "error",
        };
        dispatch({
          selectionPhoto: errorPhoto,
          type: "addOrUpdateSelectionPhoto",
        });
        return errorPhoto;
      });
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.error("Could not add photo", file);
  }
};

export const readFileAsDataUrl = async (file: File): Promise<string> => {
  return new Promise((success, failure) => {
    const reader = new FileReader();

    reader.addEventListener(
      "load",
      () => {
        const { result } = reader;
        if (result) {
          success(result as string);
        } else {
          failure();
        }
      },
      false
    );

    reader.readAsDataURL(file);
  });
};

export const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
  return new Promise((success, failure) => {
    const reader = new FileReader();

    reader.addEventListener(
      "load",
      () => {
        const { result } = reader;
        if (result) {
          success(result as ArrayBuffer);
        } else {
          failure();
        }
      },
      false
    );

    reader.readAsArrayBuffer(file);
  });
};

export const calcImageSize = async (dataUrl: string): Promise<Size<Unit.PX>> => {
  return new Promise((success, failure) => {
    const image = new Image();

    image.onload = () => {
      success({
        height: {
          value: image.naturalHeight || image.height,
          unit: Unit.PX,
        } as const,
        width: {
          value: image.naturalWidth || image.width,
          unit: Unit.PX,
        } as const,
      });
    };

    image.onerror = () => {
      failure();
    };

    image.src = dataUrl;
  });
};

// See https://www.impulseadventure.com/photo/exif-orientation.html
export const getExifOrientation = (arrayBuffer: ArrayBuffer): number | null => {
  let tags;
  try {
    tags = ExifReader.load(arrayBuffer);
  } catch (e: unknown) {
    const error = e as { message: string };
    if (error?.message === "Invalid image format") {
      return null;
    }

    throw e;
  }

  if (
    tags &&
    tags.Orientation &&
    tags.Orientation.value &&
    tags.Orientation.value > 0 &&
    tags.Orientation.value < 9
  ) {
    return tags.Orientation.value;
  }

  return null;
};

export const getResizedDataUrl = async (
  dataUrl: string,
  exifOrientation: number | null,
  fitSize: number
): Promise<string> => {
  return new Promise((success, failure) => {
    const image = new Image();

    image.onload = () => {
      let width = fitSize;
      let height = fitSize;
      if (image.height >= image.width) {
        width = Math.floor(height * (image.width / image.height));
      } else {
        height = Math.floor(width * (image.height / image.width));
      }

      const canvas: HTMLCanvasElement = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const canvasContext = canvas.getContext("2d");
      if (canvasContext) {
        if (exifOrientation && exifOrientation > 4) {
          canvas.width = height;
          canvas.height = width;
        }

        switch (exifOrientation) {
          case 2:
            // horizontal flip
            canvasContext.translate(width, 0);
            canvasContext.scale(-1, 1);
            break;
          case 3:
            // 180° rotate left
            canvasContext.translate(width, height);
            canvasContext.rotate(Math.PI);
            break;
          case 4:
            // vertical flip
            canvasContext.translate(0, height);
            canvasContext.scale(1, -1);
            break;
          case 5:
            // vertical flip + 90 rotate right
            canvasContext.rotate(0.5 * Math.PI);
            canvasContext.scale(1, -1);
            break;
          case 6:
            // 90° rotate right
            canvasContext.rotate(0.5 * Math.PI);
            canvasContext.translate(0, -height);
            break;
          case 7:
            // horizontal flip + 90 rotate right
            canvasContext.rotate(0.5 * Math.PI);
            canvasContext.translate(width, -height);
            canvasContext.scale(-1, 1);
            break;
          case 8:
            // 90° rotate left
            canvasContext.rotate(-0.5 * Math.PI);
            canvasContext.translate(-width, 0);
            break;
        }

        canvasContext.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);

        const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.85);
        success(resizedDataUrl);
      }

      failure();
    };

    image.onerror = () => {
      failure();
    };

    image.src = dataUrl;
  });
};
